import { useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { adminApi } from "@/admin/adminApi";
import AdminLayout from "@/admin/AdminLayout";
import { toast } from "@/admin/Toast";

const STATUSES = ["all", "new", "read", "archived"];

export default function ContactSubmissionsPage() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("all");
  const [detail, setDetail] = useState(null);

  async function reload(sel = status) {
    const params = sel === "all" ? {} : { status_filter: sel };
    const { data } = await adminApi.get("/contact-submissions", { params });
    setItems(data);
  }
  useEffect(() => {
    reload(status);
  }, [status]);

  async function setSubStatus(id, next) {
    try {
      await adminApi.patch(`/contact-submissions/${id}`, { status: next });
      toast("success", `Marked as ${next}`);
      reload(status);
    } catch (e) {
      toast("error", "Update failed");
    }
  }
  async function deleteSub(id) {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await adminApi.delete(`/contact-submissions/${id}`);
      toast("success", "Deleted");
      setDetail(null);
      reload(status);
    } catch (e) {
      toast("error", "Delete failed");
    }
  }

  return (
    <AdminLayout title="Contact submissions">
      <div className="flex items-center gap-2 mb-5">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`inbox-tab-${s}`}
            className={`px-4 h-9 text-[11px] uppercase tracking-[0.22em] rounded-full transition ${
              status === s ? "bg-mist-navy text-white" : "bg-white border border-mist-soft text-mist-navy hover:border-mist-gold"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {!items ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-mist-navy" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-mist-soft rounded-md p-10 text-center text-sm text-mist-ink/60">
          No submissions.
        </div>
      ) : (
        <div className="bg-white border border-mist-soft rounded-md overflow-hidden" data-testid="inbox-list">
          <table className="w-full text-sm">
            <thead className="bg-mist-off text-[11px] uppercase tracking-[0.22em] text-mist-navy/60">
              <tr>
                <th className="px-4 py-3 text-left">Received</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Subject</th>
                <th className="px-4 py-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr
                  key={s.id}
                  className="border-t border-mist-soft/70 hover:bg-mist-off/50 cursor-pointer"
                  onClick={() => {
                    setDetail(s);
                    if (s.status === "new") setSubStatus(s.id, "read");
                  }}
                  data-testid={`inbox-row-${s.id}`}
                >
                  <td className="px-4 py-3 text-xs text-mist-ink/60">{(s.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3">{s.full_name}</td>
                  <td className="px-4 py-3 text-mist-navy">{s.email}</td>
                  <td className="px-4 py-3 truncate max-w-xs">{s.subject}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                        s.status === "new" ? "bg-mist-gold/20 text-mist-navy" :
                        s.status === "archived" ? "bg-mist-ink/10 text-mist-ink/60" :
                        "bg-emerald-100 text-emerald-800"
                      }`}
                    >
                      {s.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 bg-mist-navy/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setDetail(null)}>
          <div
            className="bg-white rounded-md p-6 max-w-2xl w-full shadow-2xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="inbox-detail"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl text-mist-navy">{detail.subject}</h3>
              <button onClick={() => setDetail(null)} className="text-mist-ink/60 hover:text-mist-navy text-2xl">×</button>
            </div>
            <div className="text-xs text-mist-ink/60 mb-4">
              From <span className="text-mist-navy font-medium">{detail.full_name}</span> · <a href={`mailto:${detail.email}`} className="text-mist-navy hover:text-mist-gold">{detail.email}</a>
              {detail.phone && <> · {detail.phone}</>}
              <div className="mt-1">{detail.created_at}</div>
              <div className="mt-1">IP {detail.ip} · UA {(detail.user_agent || "").slice(0, 60)}…</div>
            </div>
            <div className="text-sm text-mist-ink whitespace-pre-wrap bg-mist-off border border-mist-soft rounded-md p-4">
              {detail.message}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {detail.status !== "read" && (
                <button onClick={() => setSubStatus(detail.id, "read")} data-testid="inbox-mark-read" className="text-xs uppercase tracking-widest bg-white border border-mist-soft px-4 h-9 rounded-full hover:border-mist-gold">Mark read</button>
              )}
              {detail.status !== "archived" && (
                <button onClick={() => setSubStatus(detail.id, "archived")} data-testid="inbox-archive" className="text-xs uppercase tracking-widest bg-white border border-mist-soft px-4 h-9 rounded-full hover:border-mist-gold">Archive</button>
              )}
              <button onClick={() => deleteSub(detail.id)} data-testid="inbox-delete" className="ml-auto text-xs uppercase tracking-widest bg-red-50 text-red-700 border border-red-200 px-4 h-9 rounded-full hover:bg-red-100 inline-flex items-center gap-1.5"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
